import { STAGE_LABELS } from '@nemo/core/model';
import type { Candidate } from '../dealroom/types.js';

/** How many sectors and regions a row names before the rest go to the hover. */
const SHOWN_PER_KIND = 2;

/**
 * What a candidate's own rounds of the last 36 months say, in a few chips:
 * the stages it led, then its strongest sectors and regions
 * ("Led Series A ×3 · Health · DACH"). Every fact and its round count is on
 * hover. Nothing at all for a name whose rounds say nothing.
 */
export function FactLine(props: { candidate: Candidate }): React.JSX.Element | null {
  const { facts } = props.candidate;
  const chips = [
    ...facts.leadsAt.map(
      (fact) => `Led ${STAGE_LABELS[fact.value]}${fact.rounds > 1 ? ` ×${fact.rounds}` : ''}`,
    ),
    ...facts.sectors.slice(0, SHOWN_PER_KIND).map((fact) => fact.value),
    ...facts.regions.slice(0, SHOWN_PER_KIND).map((fact) => fact.value),
  ];
  if (chips.length === 0) return null;
  return (
    <span className="fact-line" title={factsText(props.candidate)}>
      {chips.map((chip) => (
        <span className="chip fact-chip" key={chip}>
          {chip}
        </span>
      ))}
    </span>
  );
}

export function factsText(candidate: Candidate): string {
  const { facts } = candidate;
  const line = (label: string, entries: readonly { name: string; rounds: number }[]): string[] =>
    entries.length === 0
      ? []
      : [`${label}: ${entries.map((entry) => `${entry.name} (${entry.rounds})`).join(', ')}`];
  return [
    'Rounds in the last 36 months',
    ...line(
      'Invests at',
      facts.investsAt.map((fact) => ({ name: STAGE_LABELS[fact.value], rounds: fact.rounds })),
    ),
    ...line(
      'Led',
      facts.leadsAt.map((fact) => ({ name: STAGE_LABELS[fact.value], rounds: fact.rounds })),
    ),
    ...line(
      'Sectors',
      facts.sectors.map((fact) => ({ name: fact.value, rounds: fact.rounds })),
    ),
    ...line(
      'Regions',
      facts.regions.map((fact) => ({ name: fact.value, rounds: fact.rounds })),
    ),
  ].join('\n');
}
